/**
 * The opened card's detail — its **stage-event history** and the **reject-with-reason**
 * action, WP-W3.
 *
 * ── History is append-only and read in the order it happened ─────────────────
 *
 * `GET …/stage-events` answers chronological (oldest first), the order the pipeline
 * actually moved, and this renders it in exactly that order: the first row is the
 * `applied` event (`from_stage` null), each later move adds one. Every row says
 * from → to, who moved it, when, and the note if there was one — a reject's reason is
 * the note the server recorded, so it appears here without a second read.
 *
 * ── Reject is a transition with a reason ─────────────────────────────────────
 *
 * `POST …/reject` is a convenience over a transition to `rejected` that also stamps
 * `rejected_reason` and records it as the history note. The reason is **optional** (the
 * contract's `reason` has no `required`), so the button is never gated on it — but the
 * reason, when given, is the thing the next person reads, so the field is offered
 * first. A terminal application cannot be rejected again; the server answers `409` and
 * that refusal is rendered rather than pre-empted.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { Locale } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { RecruitingStageEvent } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { isApplicationStage, STAGE_LABEL } from '../../recruiting/application';
import type { QueryStatus } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const REASON_MAX = 2000;

export interface ApplicationHistoryPanelProps {
  readonly status: QueryStatus;
  readonly events: readonly RecruitingStageEvent[] | null;
  readonly readError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly locale: Locale;
  readonly timezone: string;
  /** Whether a reject is possible — `false` for a terminal application (no reject action). */
  readonly canReject: boolean;
  readonly rejectPending: boolean;
  readonly rejectError: WorkforceApiError | null;
  readonly rejected: boolean;
  readonly onReject: (reason: string) => void;
  /**
   * The opened application's stage controls. They live here rather than on every
   * name in every column: a move belongs to the one application a recruiter has
   * opened, and seven columns of transition buttons is a wall that gets read past.
   */
  readonly children?: ReactNode;
}

export function ApplicationHistoryPanel({
  status,
  events,
  readError,
  onReload,
  locale,
  timezone,
  canReject,
  rejectPending,
  rejectError,
  rejected,
  onReject,
  children,
}: ApplicationHistoryPanelProps): ReactNode {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  const stageWord = (stage: string | null): string => {
    if (stage === null) return t('recruiting.pipeline.history.origin');
    return isApplicationStage(stage) ? t(STAGE_LABEL[stage]) : stage;
  };

  return (
    <section className="wp-section" aria-labelledby="w3-detail-heading" data-testid="w3-detail">
      <h3 className="wp-section__title" id="w3-detail-heading" tabIndex={-1}>
        {t('recruiting.pipeline.history.title')}
      </h3>

      {children}

      {status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('recruiting.pipeline.history.subject')}
          testId="w3-history-skeleton"
        />
      ) : null}
      {status === 'error' && readError !== null ? (
        <ApiErrorNote
          error={readError}
          onRetry={onReload}
          subject={t('recruiting.pipeline.history.subject')}
          testId="w3-history-error"
        />
      ) : null}

      {events === null ? null : events.length === 0 ? (
        <EmptyState
          title={t('recruiting.pipeline.history.empty.title')}
          description={t('recruiting.pipeline.history.empty.body')}
          testId="w3-history-empty"
        />
      ) : (
        <ol className="wp-rows" data-testid="w3-history">
          {events.map((event) => (
            <li className="wp-row" key={event.id} data-testid={`w3-history-${event.id}`}>
              <div className="wp-row__main">
                <p>
                  {t('recruiting.pipeline.history.move', {
                    from: stageWord(event.from_stage),
                    to: stageWord(event.to_stage),
                  })}
                </p>
                <p className="wp-small wp-muted">
                  {t('recruiting.pipeline.history.meta', {
                    who: event.moved_by ?? t('recruiting.pipeline.history.system'),
                    when: formatInstant(event.created_at, locale, timezone),
                  })}
                </p>
                {event.note === null ? null : (
                  <p className="wp-small" data-testid={`w3-history-note-${event.id}`}>
                    {t('recruiting.pipeline.history.note', { note: event.note })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {canReject ? (
        <section
          className="wp-section__subsection"
          aria-labelledby="w3-reject-heading"
          data-testid="w3-reject"
        >
          <h4 className="wp-section__title" id="w3-reject-heading">
            {t('recruiting.pipeline.reject.title')}
          </h4>
          <p className="wp-small wp-muted">{t('recruiting.pipeline.reject.intro')}</p>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w3-reject-reason">
              {t('recruiting.pipeline.reject.reason')}
            </label>
            <textarea
              className="wp-field__input"
              id="w3-reject-reason"
              data-testid="w3-reject-reason"
              value={reason}
              maxLength={REASON_MAX}
              rows={2}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--danger"
              type="button"
              data-testid="w3-reject-submit"
              disabled={rejectPending}
              onClick={() => {
                onReject(reason.trim());
              }}
            >
              {rejectPending
                ? t('recruiting.pipeline.reject.pending')
                : t('recruiting.pipeline.reject.submit')}
            </button>
          </div>
          {rejectError === null ? null : (
            <ApiErrorNote
              error={rejectError}
              subject={t('recruiting.pipeline.subjectWord')}
              testId="w3-reject-error"
            />
          )}
        </section>
      ) : (
        <StatusNote
          severity="info"
          title={t('recruiting.pipeline.reject.terminal')}
          testId="w3-reject-terminal"
        />
      )}
      {rejected ? (
        <StatusNote
          severity="low"
          title={t('recruiting.pipeline.reject.ok')}
          testId="w3-reject-ok"
        />
      ) : null}
    </section>
  );
}
